import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { toast } from "sonner";
import { useAuth } from "@/contexts/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";
import { Send, Users2, Upload, Eye, Code2, AlertCircle, Loader2 } from "lucide-react";
import RichTextEditor from "@/components/RichTextEditor";

const SHORTCODES = ["{{full_name}}", "{{first_name}}", "{{email}}", "{{company}}"];

// RFC-5321-ish minimum email sanity check — good enough to catch typos client-side.
const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

/** Parse CSV text into recipient rows. De-dupes, drops invalid emails,
 * returns { valid, invalid, duplicates } counts + the cleaned list. */
function parseAndClean(text) {
  const stats = { total: 0, valid: [], invalid: 0, duplicates: 0 };
  if (!text.trim()) return stats;
  const lines = text.split(/\r?\n/).filter((l) => l.trim());
  const first = lines[0].split(",").map((s) => s.trim().toLowerCase());
  const hasHeader = first.includes("email");
  const cols = hasHeader ? first : ["email", "full_name", "company"];
  const rows = hasHeader ? lines.slice(1) : lines;
  const seen = new Set();
  for (const line of rows) {
    stats.total += 1;
    const parts = line.split(",").map((s) => s.trim());
    const row = {};
    cols.forEach((c, i) => { row[c] = parts[i] || ""; });
    const email = (row.email || "").trim().toLowerCase();
    if (!email || !EMAIL_RE.test(email)) { stats.invalid += 1; continue; }
    if (seen.has(email)) { stats.duplicates += 1; continue; }
    seen.add(email);
    const { email: _e, full_name = "", company = "", ...custom } = row;
    stats.valid.push({ email, full_name, company, custom });
  }
  return stats;
}

/** Extract a readable message from FastAPI 400/422 responses (Pydantic array or string). */
function extractError(err) {
  const d = err?.response?.data?.detail;
  if (!d) return err?.message || "Something went wrong. Please try again.";
  if (typeof d === "string") return d;
  if (Array.isArray(d)) {
    return d.map((x) => `${(x.loc || []).slice(1).join(".")}: ${x.msg}`).join(" · ");
  }
  return "Validation failed";
}

export default function CampaignEditor({ initial, onClose, onSaved }) {
  const { activeCompanyId } = useAuth();
  const navigate = useNavigate();
  const isEdit = !!initial?.id;
  const [profiles, setProfiles] = useState([]);
  const [templates, setTemplates] = useState([]);
  const [companies, setCompanies] = useState([]);
  const [contacts, setContacts] = useState([]);
  const [mode, setMode] = useState("visual");
  const [csvRaw, setCsvRaw] = useState("");
  const [scheduleOn, setScheduleOn] = useState(false);
  const [saving, setSaving] = useState(false);
  const [savingAction, setSavingAction] = useState(""); // "draft" | "start"
  const [error, setError] = useState("");
  const [testTo, setTestTo] = useState("");

  const empty = {
    company_id: activeCompanyId || "",
    name: "",
    smtp_profile_id: "",
    from_email: "",
    from_name: "",
    reply_to: "",
    subject: "",
    body_html: "",
    body_text: "",
    schedule_at: "",
    throttle_per_hour: 300,
    tags: [],
  };
  const [form, setForm] = useState(() => ({ ...empty, ...initial }));

  useEffect(() => {
    setForm({ ...empty, ...initial });
    setScheduleOn(!!initial?.schedule_at);
    setCsvRaw((initial?.recipients || []).map((r) => [r.email, r.full_name, r.company].join(",")).join("\n"));
    setError("");
    // eslint-disable-next-line
  }, [initial]);

  // Fetch companies once so we can auto-pick when there's only one.
  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/companies");
        const active = data.filter((c) => c.status !== "archived");
        setCompanies(active);
        // Auto-pick company: prefer activeCompanyId, fall back to sole company.
        if (!form.company_id) {
          const pick = active.find((c) => c.id === activeCompanyId) || (active.length === 1 ? active[0] : null);
          if (pick) setForm((f) => ({ ...f, company_id: pick.id }));
        }
      } catch (e) { setError(extractError(e)); }
    })();
    // eslint-disable-next-line
  }, []);

  // Fetch SMTP profiles + templates + contacts ONLY once a company is chosen.
  useEffect(() => {
    if (!form.company_id) {
      setProfiles([]); setTemplates([]); setContacts([]); return;
    }
    (async () => {
      try {
        const params = { company_id: form.company_id };
        const [p, t, ct] = await Promise.all([
          api.get("/bulk/smtp-profiles", { params }),
          api.get("/bulk/templates", { params }),
          api.get("/contacts", { params }),
        ]);
        setProfiles(p.data); setTemplates(t.data); setContacts(ct.data);
        // Auto-pick sole profile
        if (!form.smtp_profile_id && p.data.length === 1) {
          const only = p.data[0];
          setForm((f) => ({
            ...f, smtp_profile_id: only.id,
            from_email: f.from_email || only.from_email || "",
            from_name: f.from_name || only.from_name || "",
          }));
        }
      } catch (e) { setError(extractError(e)); }
    })();
    // eslint-disable-next-line
  }, [form.company_id]);

  const applyTemplate = (id) => {
    const t = templates.find((x) => x.id === id);
    if (!t) return;
    setForm((f) => ({ ...f, subject: t.subject || f.subject, body_html: t.body_html || f.body_html, body_text: t.body_text || f.body_text }));
    toast.success("Template applied");
  };

  const applyProfile = (id) => {
    const p = profiles.find((x) => x.id === id);
    setForm((f) => ({ ...f, smtp_profile_id: id, from_email: f.from_email || p?.from_email || "", from_name: f.from_name || p?.from_name || "" }));
  };

  const cleaned = useMemo(() => parseAndClean(csvRaw), [csvRaw]);
  const recipients = cleaned.valid;

  const importAllContacts = () => {
    if (contacts.length === 0) { toast.error("No contacts in this company yet."); return; }
    const lines = ["email,full_name,company"];
    contacts.forEach((c) => lines.push(`${c.email},${(c.full_name || "").replace(/,/g, " ")},${(c.customer_company || "").replace(/,/g, " ")}`));
    setCsvRaw(lines.join("\n"));
    toast.success(`Imported ${contacts.length} contacts`);
  };

  const uploadCsv = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 20 * 1024 * 1024) { toast.error("CSV too large (max 20 MB)"); return; }
    const text = await file.text();
    setCsvRaw(text);
    toast.success(`Loaded ${file.name}`);
  };

  // ---------- Validation gates ----------
  const validationErrors = useMemo(() => {
    const errs = [];
    if (!form.company_id) errs.push("Company is required");
    if (!form.name?.trim()) errs.push("Campaign name is required");
    if (!form.smtp_profile_id) errs.push("Choose an SMTP profile");
    if (!form.from_email || !EMAIL_RE.test(form.from_email)) errs.push("Valid From email is required");
    if (!form.subject?.trim()) errs.push("Subject is required");
    if (!form.body_html?.trim() && !form.body_text?.trim()) errs.push("Email body is empty");
    if (recipients.length === 0) errs.push("Add at least one valid recipient");
    if (scheduleOn && !form.schedule_at) errs.push("Pick a scheduled send time");
    return errs;
  }, [form, recipients.length, scheduleOn]);

  // Drafts don't require recipients/schedule — only the fields needed to persist a meaningful row.
  const draftErrors = useMemo(() => {
    const errs = [];
    if (!form.company_id) errs.push("Company is required");
    if (!form.name?.trim()) errs.push("Campaign name is required");
    if (!form.smtp_profile_id) errs.push("Choose an SMTP profile");
    if (!form.from_email || !EMAIL_RE.test(form.from_email)) errs.push("Valid From email is required");
    if (!form.subject?.trim()) errs.push("Subject is required");
    return errs;
  }, [form]);

  const canSubmit = !saving && validationErrors.length === 0;
  const canSaveDraft = !saving && draftErrors.length === 0;

  const submit = async (start = false) => {
    setError("");
    // Full validation for a live send; lighter validation for drafts.
    const errs = start ? validationErrors : draftErrors;
    if (errs.length > 0) {
      setError(errs[0]);
      return;
    }
    setSaving(true);
    setSavingAction(start ? "start" : "draft");
    try {
      const scheduleIso = scheduleOn && form.schedule_at ? new Date(form.schedule_at).toISOString() : null;
      let cid = form.id;
      if (isEdit) {
        await api.patch(`/bulk/campaigns/${cid}`, {
          name: form.name, smtp_profile_id: form.smtp_profile_id,
          from_email: form.from_email, from_name: form.from_name, reply_to: form.reply_to,
          subject: form.subject, body_html: form.body_html, body_text: form.body_text,
          schedule_at: scheduleIso, throttle_per_hour: form.throttle_per_hour, tags: form.tags,
        });
      } else {
        const payload = {
          company_id: form.company_id, name: form.name.trim(), smtp_profile_id: form.smtp_profile_id,
          from_email: form.from_email.trim(), from_name: form.from_name || "", reply_to: form.reply_to || "",
          subject: form.subject.trim(), body_html: form.body_html || "", body_text: form.body_text || "",
          recipients, schedule_at: scheduleIso,
          throttle_per_hour: form.throttle_per_hour || 300, tags: form.tags || [],
        };
        const { data } = await api.post("/bulk/campaigns", payload);
        cid = data.id;
      }
      if (start && cid) {
        await api.post(`/bulk/campaigns/${cid}/start`);
        toast.success(`Campaign started — ${recipients.length} recipient${recipients.length !== 1 ? "s" : ""} queued`);
      } else {
        toast.success(scheduleOn ? "Campaign scheduled" : "Draft saved");
      }
      onSaved?.();
      onClose();
      // Redirect to the campaign detail page so user always sees what happened.
      if (cid) setTimeout(() => navigate(`/bulk/campaigns/${cid}`), 250);
    } catch (e) {
      const msg = extractError(e);
      setError(msg);
      toast.error(msg);
    } finally {
      setSaving(false);
      setSavingAction("");
    }
  };

  const sendTest = async () => {
    if (!testTo || !EMAIL_RE.test(testTo)) { toast.error("Enter a valid test email"); return; }
    // Dedicated field checks — a test send needs less than a full campaign.
    const missing = [];
    if (!form.company_id) missing.push("Company");
    if (!form.smtp_profile_id) missing.push("SMTP profile");
    if (!form.from_email || !EMAIL_RE.test(form.from_email)) missing.push("From email");
    if (!form.subject?.trim()) missing.push("Subject");
    if (!form.body_html?.trim() && !form.body_text?.trim()) missing.push("Body");
    if (missing.length) { toast.error(`Fill ${missing.join(", ")} first`); return; }
    setError("");
    try {
      setSaving(true); setSavingAction("test");
      await api.post("/bulk/campaigns/test-send", {
        company_id: form.company_id, smtp_profile_id: form.smtp_profile_id,
        from_email: form.from_email, from_name: form.from_name || "",
        reply_to: form.reply_to || "",
        subject: form.subject, body_html: form.body_html || "", body_text: form.body_text || "",
        to: testTo,
      });
      toast.success(`Test sent to ${testTo}`);
    } catch (e) {
      const msg = extractError(e);
      setError(msg);        // persistent inline banner — not just a toast
      toast.error(msg);
    } finally { setSaving(false); setSavingAction(""); }
  };

  return (
    <Dialog open onOpenChange={(o) => { if (!o && !saving) onClose(); }}>
      <DialogContent className="rounded-sm max-w-4xl max-h-[92vh] overflow-y-auto" data-testid="campaign-editor">
        <DialogHeader>
          <DialogTitle className="font-display text-2xl tracking-tight flex items-center gap-2">
            <Send className="h-5 w-5 text-emerald-brand" /> {isEdit ? "Edit" : "New"} campaign
          </DialogTitle>
          <DialogDescription>Compose, choose recipients, then save as draft or send now. Your progress persists on errors.</DialogDescription>
        </DialogHeader>

        {error && (
          <div className="rounded-sm border border-crimson/30 bg-crimson/[0.05] p-3 flex items-start gap-2" data-testid="campaign-error">
            <AlertCircle className="h-4 w-4 text-crimson shrink-0 mt-0.5" />
            <div className="text-xs text-crimson">{error}</div>
          </div>
        )}

        <div className="grid grid-cols-1 lg:grid-cols-[1.5fr_1fr] gap-4">
          <div className="space-y-3">
            {!isEdit && (
              <F label={<span>Company <span className="text-crimson">*</span></span>}>
                <Select value={form.company_id} onValueChange={(v) => setForm({ ...form, company_id: v, smtp_profile_id: "", from_email: "", from_name: "" })}>
                  <SelectTrigger className="rounded-sm" data-testid="campaign-company-select"><SelectValue placeholder="Pick a company first" /></SelectTrigger>
                  <SelectContent>
                    {companies.length === 0 && <div className="p-2 text-xs text-muted-foreground">No active companies</div>}
                    {companies.map((c) => <SelectItem key={c.id} value={c.id}>{c.trading_name || c.legal_name}</SelectItem>)}
                  </SelectContent>
                </Select>
              </F>
            )}
            <F label={<span>Campaign name <span className="text-crimson">*</span></span>}>
              <Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} required data-testid="campaign-name-input" className="rounded-sm" />
            </F>
            <div className="grid grid-cols-2 gap-3">
              <F label={<span>SMTP profile <span className="text-crimson">*</span></span>}>
                <Select value={form.smtp_profile_id} onValueChange={applyProfile} disabled={!form.company_id}>
                  <SelectTrigger className="rounded-sm" data-testid="campaign-smtp-select"><SelectValue placeholder={form.company_id ? "Choose profile" : "Pick company first"} /></SelectTrigger>
                  <SelectContent>
                    {profiles.length === 0 && form.company_id && <div className="p-2 text-xs text-muted-foreground">No SMTP profiles for this company. Create one under Bulk → SMTP Profiles.</div>}
                    {profiles.map((p) => <SelectItem key={p.id} value={p.id}>{p.name} · {p.host}</SelectItem>)}
                  </SelectContent>
                </Select>
              </F>
              <F label="Template (optional)">
                <Select value="" onValueChange={applyTemplate} disabled={!form.company_id}>
                  <SelectTrigger className="rounded-sm" data-testid="campaign-template-select"><SelectValue placeholder="Apply template" /></SelectTrigger>
                  <SelectContent>
                    {templates.length === 0 && <div className="p-2 text-xs text-muted-foreground">No saved templates</div>}
                    {templates.map((t) => <SelectItem key={t.id} value={t.id}>{t.name}</SelectItem>)}
                  </SelectContent>
                </Select>
              </F>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <F label={<span>From email <span className="text-crimson">*</span></span>}>
                <Input type="email" value={form.from_email} onChange={(e) => setForm({ ...form, from_email: e.target.value })} required data-testid="campaign-from-email" className="rounded-sm" />
              </F>
              <F label="From name">
                <Input value={form.from_name} onChange={(e) => setForm({ ...form, from_name: e.target.value })} data-testid="campaign-from-name" className="rounded-sm" />
              </F>
            </div>
            <F label="Reply-to (optional)">
              <Input type="email" value={form.reply_to} onChange={(e) => setForm({ ...form, reply_to: e.target.value })} data-testid="campaign-reply-to" className="rounded-sm" />
            </F>
            <F label={<span>Subject <span className="text-crimson">*</span></span>}>
              <Input value={form.subject} onChange={(e) => setForm({ ...form, subject: e.target.value })} required data-testid="campaign-subject" className="rounded-sm" placeholder="Hi {{first_name}} — a quick update" />
            </F>
            <div className="flex items-center gap-2 flex-wrap">
              <span className="text-[10px] font-mono uppercase tracking-widest text-muted-foreground">Shortcodes:</span>
              {SHORTCODES.map((s) => (
                <button key={s} type="button" onClick={() => navigator.clipboard.writeText(s).then(() => toast.success(`Copied ${s}`))} className="text-[11px] font-mono px-2 py-0.5 rounded bg-muted hover:bg-emerald-brand/12 hover:text-emerald-brand">{s}</button>
              ))}
            </div>
            <div className="flex items-center justify-between">
              <Label className="text-[10px] font-mono uppercase tracking-widest text-muted-foreground">Body</Label>
              <div className="inline-flex rounded-sm border border-border overflow-hidden text-[10px] font-mono uppercase">
                <button type="button" onClick={() => setMode("visual")} className={`px-2 py-1 flex items-center gap-1 ${mode === "visual" ? "bg-emerald-brand/15 text-emerald-brand" : "text-muted-foreground"}`}><Eye className="h-3 w-3" /> Visual</button>
                <button type="button" onClick={() => setMode("html")} className={`px-2 py-1 flex items-center gap-1 ${mode === "html" ? "bg-emerald-brand/15 text-emerald-brand" : "text-muted-foreground"}`}><Code2 className="h-3 w-3" /> HTML</button>
              </div>
            </div>
            {mode === "visual"
              ? <RichTextEditor value={form.body_html} onChange={(v) => setForm({ ...form, body_html: v })} placeholder="Design your email…" />
              : <Textarea rows={10} value={form.body_html} onChange={(e) => setForm({ ...form, body_html: e.target.value })} className="rounded-sm font-mono text-xs" data-testid="campaign-html-input" />
            }
            <F label="Plain-text fallback (optional)">
              <Textarea rows={3} value={form.body_text} onChange={(e) => setForm({ ...form, body_text: e.target.value })} className="rounded-sm text-xs" data-testid="campaign-text-input" placeholder="Shown to email clients that don't render HTML." />
            </F>
            <div className="grid grid-cols-2 gap-3">
              <F label="Throttle (emails/hr)">
                <Input type="number" min={1} max={100000} value={form.throttle_per_hour} onChange={(e) => setForm({ ...form, throttle_per_hour: Number(e.target.value) })} data-testid="campaign-throttle" className="rounded-sm" />
              </F>
              <F label="Tags (comma)">
                <Input value={(form.tags || []).join(",")} onChange={(e) => setForm({ ...form, tags: e.target.value.split(",").map((t) => t.trim()).filter(Boolean) })} className="rounded-sm" />
              </F>
            </div>
            <div className="flex items-center justify-between border border-border rounded-sm px-3 py-2.5">
              <div>
                <div className="text-sm font-medium">Schedule this campaign</div>
                <div className="text-[11px] text-muted-foreground">Send at a specific date & time (UTC) instead of immediately.</div>
              </div>
              <Switch checked={scheduleOn} onCheckedChange={setScheduleOn} data-testid="campaign-schedule-toggle" />
            </div>
            {scheduleOn && <F label={<span>Send at (UTC) <span className="text-crimson">*</span></span>}>
              <Input type="datetime-local" value={form.schedule_at?.slice(0, 16) || ""} onChange={(e) => setForm({ ...form, schedule_at: e.target.value })} data-testid="campaign-schedule-at" className="rounded-sm" />
            </F>}
          </div>

          <div className="space-y-3">
            <div className="rounded-sm border border-border bg-white p-4">
              <div className="flex items-center justify-between">
                <div>
                  <div className="text-[10px] font-mono uppercase tracking-widest text-muted-foreground">Recipients (valid)</div>
                  <div className="font-display text-3xl font-semibold mt-0.5" data-testid="recipients-count">{recipients.length}</div>
                </div>
                <div className="flex flex-col gap-1.5">
                  <Button size="sm" variant="outline" className="rounded-sm" onClick={importAllContacts} type="button" data-testid="import-contacts-button"><Users2 className="h-3.5 w-3.5 mr-1" /> Import contacts</Button>
                  <label className="inline-flex items-center gap-1 border border-border rounded-sm px-2 py-1 text-xs cursor-pointer hover:bg-muted" data-testid="upload-csv-button">
                    <Upload className="h-3.5 w-3.5" /> Upload CSV
                    <input type="file" accept=".csv,text/csv" onChange={uploadCsv} className="hidden" />
                  </label>
                </div>
              </div>
              <Textarea
                rows={9}
                value={csvRaw}
                onChange={(e) => setCsvRaw(e.target.value)}
                placeholder={"email,full_name,company\njane@acme.com,Jane Doe,Acme"}
                className="mt-3 rounded-sm font-mono text-[11px]"
                data-testid="campaign-recipients-input"
              />
              <div className="text-[10px] text-muted-foreground mt-1 font-mono">Header row optional · required column: email · optional: full_name, company + custom columns</div>
              {(cleaned.invalid > 0 || cleaned.duplicates > 0) && (
                <div className="mt-2 text-[10px] font-mono text-yellow-700 bg-yellow-500/[0.08] rounded-sm px-2 py-1" data-testid="cleanup-notice">
                  Cleaned: {cleaned.invalid} invalid · {cleaned.duplicates} duplicate email{cleaned.duplicates !== 1 ? "s" : ""} skipped
                </div>
              )}
              {recipients.length > 0 && (
                <div className="mt-3 text-[11px] text-muted-foreground max-h-24 overflow-y-auto font-mono">
                  {recipients.slice(0, 5).map((r, i) => <div key={i}>• {r.email} — {r.full_name || "—"}{r.company ? ` · ${r.company}` : ""}</div>)}
                  {recipients.length > 5 && <div>… +{recipients.length - 5} more</div>}
                </div>
              )}
            </div>

            {/* Send a test email to yourself */}
            <div className="rounded-sm border border-border bg-white p-3">
              <div className="text-[10px] font-mono uppercase tracking-widest text-muted-foreground mb-1.5">Send test email</div>
              <div className="flex gap-1.5">
                <Input type="email" value={testTo} onChange={(e) => setTestTo(e.target.value)} placeholder="you@yourcompany.com" className="rounded-sm h-8 text-xs" data-testid="campaign-test-to" />
                <Button size="sm" variant="outline" className="rounded-sm" onClick={sendTest} disabled={saving || !form.smtp_profile_id} data-testid="campaign-test-send">
                  {savingAction === "test" ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : "Send"}
                </Button>
              </div>
            </div>

            {validationErrors.length > 0 && (
              <div className="rounded-sm border border-yellow-500/30 bg-yellow-500/[0.05] p-2.5 space-y-1" data-testid="validation-summary">
                <div className="text-[10px] font-mono uppercase tracking-widest text-yellow-700 flex items-center gap-1">
                  <AlertCircle className="h-3 w-3" /> Fix before saving
                </div>
                <ul className="text-xs text-yellow-800 list-disc list-inside">
                  {validationErrors.map((v) => <li key={v}>{v}</li>)}
                </ul>
              </div>
            )}

            <div className="space-y-2">
              <Button
                className="w-full rounded-sm h-11 shadow-md bg-emerald-brand hover:bg-emerald-brand/90"
                onClick={() => submit(true)}
                disabled={!canSubmit}
                type="button"
                data-testid="campaign-start-button"
              >
                {savingAction === "start"
                  ? <><Loader2 className="h-4 w-4 mr-1.5 animate-spin" /> Starting…</>
                  : <><Send className="h-4 w-4 mr-1.5" /> Save & start</>}
              </Button>
              <Button
                variant="outline"
                className="w-full rounded-sm"
                onClick={() => submit(false)}
                disabled={!canSaveDraft}
                type="button"
                data-testid="campaign-save-draft-button"
              >
                {savingAction === "draft"
                  ? <><Loader2 className="h-4 w-4 mr-1.5 animate-spin" /> Saving…</>
                  : `Save as ${scheduleOn ? "scheduled" : "draft"}`}
              </Button>
            </div>
          </div>
        </div>

        <DialogFooter />
      </DialogContent>
    </Dialog>
  );
}

function F({ label, children }) {
  return <div className="space-y-1.5"><Label className="text-[10px] font-mono uppercase tracking-widest text-muted-foreground">{label}</Label>{children}</div>;
}
